import { useState, useEffect } from 'react';

/**
 * useMediaQuery Hook
 *
 * Custom hook to detect media query matches (for responsive design)
 *
 * @param query - CSS media query string (e.g., '(max-width: 768px)')
 * @returns boolean - true if media query matches
 *
 * @example
 * const isMobile = useMediaQuery('(max-width: 768px)');
 * const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1024px)');
 * const isDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    // SSR safety: return false during server-side rendering
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    // SSR safety: skip effect during server-side rendering
    if (typeof window === 'undefined') {
      return;
    }

    const media = window.matchMedia(query);

    // Update state if initial value was different
    if (media.matches !== matches) {
      setMatches(media.matches);
    }

    // Listener for changes
    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Modern browsers
    if (media.addEventListener) {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    }
    // Legacy browsers (Safari < 14)
    else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (media as any).addListener(listener);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return () => (media as any).removeListener(listener);
    }
  }, [query, matches]);

  return matches;
}
